import React, { useEffect, useState } from "react";
import { AppShell } from "../../components/Sidebar";
import { api } from "../../lib/api";
import { Plus, Users, Eye, Copy, Check, Search, Pencil, Trash2, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const initials = (n = "U") => n.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

const EMPTY_FORM = {
  school_id: "", roll_no: "", first_name: "", last_name: "",
  date_of_birth: "", grade: "8", division: "B", email: "", mobile: "",
};

export default function AdminStudents() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [credView, setCredView] = useState(null);
  const [creds, setCreds] = useState(null);
  const [copied, setCopied] = useState(false);
  const [schools, setSchools] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [filterSchool, setFilterSchool] = useState("");
  const [filterGrade, setFilterGrade] = useState("");
  const [filterDivision, setFilterDivision] = useState("");
  const [allStudents, setAllStudents] = useState([]);

  const load = (filters = {}) => {
    const params = {};
    if (filters.school_id) params.school_id = filters.school_id;
    if (filters.grade) params.grade = filters.grade;
    if (filters.division) params.division = filters.division;
    return api.get("/admin/students", { params }).then((r) => setItems(r.data));
  };

  useEffect(() => {
    api.get("/admin/schools").then((r) => {
      setSchools(r.data);
      setForm((f) => ({ ...f, school_id: r.data[0]?.id || "" }));
    });
    api.get("/admin/students").then((r) => setAllStudents(r.data));
  }, []);

  useEffect(() => {
    load({ school_id: filterSchool, grade: filterGrade, division: filterDivision });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSchool, filterGrade, filterDivision]);

  const clearFilters = () => {
    setFilterSchool(""); setFilterGrade(""); setFilterDivision("");
  };

  const gradeOptions = Array.from(new Set(allStudents.map((s) => s.grade).filter(Boolean))).sort();
  const divisionOptions = Array.from(new Set(allStudents.map((s) => s.division).filter(Boolean))).sort();
  const hasActiveFilters = filterSchool || filterGrade || filterDivision;

  const startNew = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, school_id: schools[0]?.id || "" });
    setOpen(true); setCreds(null);
  };

  const startEdit = (s) => {
    setEditing(s);
    setForm({
      school_id: s.school_id || "",
      roll_no: s.roll_no || "",
      first_name: s.first_name || (s.full_name || "").split(" ")[0] || "",
      last_name: s.last_name || (s.full_name || "").split(" ").slice(1).join(" ") || "",
      date_of_birth: s.date_of_birth || "",
      grade: s.grade || "8",
      division: s.division || "B",
      email: s.email || "",
      mobile: s.mobile || "",
    });
    setOpen(true); setCreds(null);
  };

  const refreshAll = () => {
    load({ school_id: filterSchool, grade: filterGrade, division: filterDivision });
    api.get("/admin/students").then((r) => setAllStudents(r.data));
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/admin/students/${editing.id}`, {
          roll_no: form.roll_no, first_name: form.first_name, last_name: form.last_name,
          email: form.email, mobile: form.mobile, grade: form.grade, division: form.division,
        });
        toast.success("Student updated");
        setOpen(false); setEditing(null); refreshAll();
      } else {
        const { data } = await api.post("/admin/students", form);
        setCreds(data);
        refreshAll();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/students/${confirmDel.id}`);
      toast.success("Student deleted");
      setConfirmDel(null); refreshAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    }
  };

  const viewCreds = async (s) => {
    try {
      const { data } = await api.get(`/admin/students/${s.id}/credentials`);
      setCredView(data);
    } catch { toast.error("Could not load credentials"); }
  };

  const copy = () => {
    if (!creds) return;
    navigator.clipboard.writeText(
      `Student ${creds.username}/${creds.password}\nParent ${creds.parent_username}/${creds.parent_password}`
    );
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const filtered = items.filter((s) =>
    s.full_name.toLowerCase().includes(search.toLowerCase()) ||
    s.username.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <AppShell role="admin">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Students</h1>
          <p className="mt-1 text-slate-500">Manage student accounts. Edit details, view credentials or remove an account.</p>
        </div>
        <button
          onClick={startNew}
          data-testid="add-student-btn"
          className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow hover:bg-indigo-700"
        >
          <Plus size={16} /> Add Student
        </button>
      </div>

      <div className="mt-6 rounded-2xl border border-slate-100 bg-white p-6 shadow-card">
        <div className="flex flex-wrap items-end gap-3">
          <div className="relative max-w-sm flex-1 min-w-[220px]">
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="search-students-admin"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search students…"
              className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            />
          </div>
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-slate-500">School</span>
            <select data-testid="filter-school" value={filterSchool} onChange={(e) => setFilterSchool(e.target.value)} className={`${inp} min-w-[160px]`}>
              <option value="">All Schools</option>
              {schools.map((sc) => <option key={sc.id} value={sc.id}>{sc.name}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-slate-500">Grade</span>
            <select data-testid="filter-grade" value={filterGrade} onChange={(e) => setFilterGrade(e.target.value)} className={`${inp} min-w-[120px]`}>
              <option value="">All Grades</option>
              {gradeOptions.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-slate-500">Division</span>
            <select data-testid="filter-division" value={filterDivision} onChange={(e) => setFilterDivision(e.target.value)} className={`${inp} min-w-[120px]`}>
              <option value="">All Divisions</option>
              {divisionOptions.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </label>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              data-testid="clear-student-filters"
              className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50"
            >
              <X size={14} /> Clear filters
            </button>
          )}
        </div>

        <div className="mt-5 overflow-x-auto">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="py-3 pr-4">Roll</th>
                <th className="py-3 pr-4">Student</th>
                <th className="py-3 pr-4">Username</th>
                <th className="py-3 pr-4">VR Code</th>
                <th className="py-3 pr-4">Class</th>
                <th className="py-3 pr-4">School</th>
                <th className="py-3 pr-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id} className="border-b border-slate-50 align-middle">
                  <td className="py-3 pr-4 font-medium text-slate-700">{s.roll_no}</td>
                  <td className="py-3 pr-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-indigo-100 text-sm font-semibold text-indigo-700">{initials(s.full_name)}</div>
                      <span className="font-medium text-slate-900">{s.full_name}</span>
                    </div>
                  </td>
                  <td className="py-3 pr-4 font-mono text-xs">{s.username}</td>
                  <td className="py-3 pr-4">
                    <span data-testid={`vr-code-${s.id}`} className="inline-flex items-center rounded-lg bg-violet-50 px-2 py-1 font-mono text-xs font-semibold tracking-wider text-violet-700">
                      {s.vr_code || "—"}
                    </span>
                  </td>
                  <td className="py-3 pr-4 text-slate-600">{s.class_label}</td>
                  <td className="py-3 pr-4 text-slate-600">{s.school_name}</td>
                  <td className="py-3 pr-4">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => viewCreds(s)} data-testid={`view-creds-student-${s.id}`} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"><Eye size={12} /></button>
                      <button onClick={() => startEdit(s)} data-testid={`edit-student-${s.id}`} className="inline-flex items-center gap-1 rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"><Pencil size={12} /></button>
                      <button onClick={() => setConfirmDel(s)} data-testid={`delete-student-${s.id}`} className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-100"><Trash2 size={12} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filtered.length === 0 && <p className="py-12 text-center text-sm text-slate-500">No students match.</p>}
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-900/60 p-4 backdrop-blur-sm">
          <div className="my-8 w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600"><Users size={18} /></div>
                <h3 className="text-xl font-bold text-slate-900">{editing ? "Edit Student" : "Add Student"}</h3>
              </div>
              <button onClick={() => { setOpen(false); setEditing(null); }} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
            </div>

            {!creds ? (
              <form onSubmit={submit} className="mt-5 space-y-4">
                <Field label="School">
                  <select required disabled={!!editing} data-testid="student-school" value={form.school_id} onChange={(e) => setForm({ ...form, school_id: e.target.value })} className={inp}>
                    {schools.map((sc) => <option key={sc.id} value={sc.id}>{`${sc.name} (${sc.code})`}</option>)}
                  </select>
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Roll No."><input required data-testid="student-roll" value={form.roll_no} onChange={(e) => setForm({ ...form, roll_no: e.target.value })} className={inp} placeholder="11" /></Field>
                  <Field label="Date of Birth (DDMM)"><input required={!editing} disabled={!!editing} data-testid="student-dob" maxLength={4} value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} className={inp} placeholder="1408" /></Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="First Name"><input required data-testid="student-first" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className={inp} /></Field>
                  <Field label="Last Name"><input required data-testid="student-last" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className={inp} /></Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Grade"><input required value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} className={inp} /></Field>
                  <Field label="Division"><input required value={form.division} onChange={(e) => setForm({ ...form, division: e.target.value.toUpperCase() })} className={inp} /></Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Email (optional)"><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inp} /></Field>
                  <Field label="Mobile (optional)"><input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className={inp} /></Field>
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <button type="button" onClick={() => { setOpen(false); setEditing(null); }} className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
                  <button data-testid="submit-student" className="rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-indigo-700">
                    {editing ? "Save Changes" : "Create Student"}
                  </button>
                </div>
              </form>
            ) : (
              <div className="mt-5 space-y-3">
                <div className="rounded-xl bg-emerald-50 p-4 text-sm">
                  <p className="font-semibold text-emerald-700">Student + parent created!</p>
                  <p className="mt-1 text-emerald-700/80">Save these credentials now — share them with the student and parent.</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 font-mono text-xs">
                  <p className="font-semibold">Student</p>
                  <p>U: {creds.username}</p>
                  <p>P: {creds.password}</p>
                  <p className="mt-2 font-semibold">Parent</p>
                  <p>U: {creds.parent_username}</p>
                  <p>P: {creds.parent_password}</p>
                </div>
                <div className="flex justify-end gap-3">
                  <button onClick={copy} className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                    {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
                  </button>
                  <button onClick={() => { setOpen(false); setCreds(null); setEditing(null); setForm({ ...EMPTY_FORM, school_id: schools[0]?.id || "" }); }} className="rounded-xl bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-700">Done</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {confirmDel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" data-testid="confirm-delete-student">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-rose-100 text-rose-600"><AlertTriangle size={18} /></div>
              <h3 className="text-xl font-bold text-slate-900">Delete "{confirmDel.full_name}"?</h3>
            </div>
            <p className="mt-3 text-sm text-slate-600">This permanently removes the student account, the linked parent account, and all VR / quiz progress. This cannot be undone.</p>
            <div className="mt-5 flex justify-end gap-3">
              <button onClick={() => setConfirmDel(null)} className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
              <button onClick={doDelete} data-testid="confirm-delete-student-confirm" className="rounded-xl bg-rose-600 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-rose-700">Yes, Delete</button>
            </div>
          </div>
        </div>
      )}

      {credView && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" data-testid="creds-modal-student">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-slate-900">Login Credentials</h3>
              <button onClick={() => setCredView(null)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
            </div>
            <p className="mt-2 text-sm text-slate-600">{credView.full_name}</p>
            <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4 font-mono text-xs">
              <p className="font-semibold">Student</p>
              <p>U: {credView.student_username}</p>
              <p>P: {credView.student_password}</p>
              {credView.parent_username && (
                <>
                  <p className="mt-2 font-semibold">Parent</p>
                  <p>U: {credView.parent_username}</p>
                  <p>P: {credView.parent_password}</p>
                </>
              )}
            </div>
            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(
                    `Student ${credView.student_username}/${credView.student_password}` +
                    (credView.parent_username ? `\nParent ${credView.parent_username}/${credView.parent_password}` : "")
                  );
                  toast.success("Copied");
                }}
                className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                <Copy size={14} /> Copy
              </button>
              <button onClick={() => setCredView(null)} className="rounded-xl bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-700">Close</button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}

const inp = "w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500";
const Field = ({ label, children }) => (<label className="block"><span className="mb-1.5 block text-sm font-medium text-slate-700">{label}</span>{children}</label>);
